import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { LogOut, MapPin, Calendar, Clock, User, Award, Printer, Download, Music, Sparkles, Trophy } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TeilnehmerDashboard = () => {
  const navigate = useNavigate();
  const printRef = useRef();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [otherAccounts, setOtherAccounts] = useState([]);
  const [fixedTexts, setFixedTexts] = useState({});
  const [uiTexts, setUiTexts] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('teilnehmer_token');
    if (!token) {
      navigate('/teilnehmer/login');
      return;
    }

    fetchDashboardData(token);
    fetchFixedTexts();
    fetchUITexts();
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      const response = await axios.get(`${API}/teilnehmer/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Dashboard API Response:', response.data);
      
      // Flatten the structure - merge teilnehmer data with root level
      // Combine both textbausteine arrays
      const allTextbausteine = [
        ...(response.data.textbausteine || []),
        ...(response.data.additional_textbausteine || [])
      ];
      
      // Extract kurs data - use kurs object if available, otherwise fallback to teilnehmer fields
      const kursData = response.data.kurs || {};
      const teilnehmerData = response.data.teilnehmer || {};
      
      const flatData = {
        ...teilnehmerData,
        modus: response.data.modus,
        textbausteine: allTextbausteine,
        pruefung_textbausteine: response.data.pruefung_textbausteine || [],
        field_visibility: response.data.field_visibility || {},
        // Kurs-Infos: Priorität hat das kurs-Objekt, dann teilnehmer-Felder
        kursort: kursData.ort || teilnehmerData.kursort || teilnehmerData.additional_data?.Kursort,
        wochentag: kursData.tag || teilnehmerData.wochentag || teilnehmerData.additional_data?.Wochentag,
        uhrzeit: kursData.zeit || teilnehmerData.uhrzeit || teilnehmerData.additional_data?.Uhrzeit,
        kursbeginn: kursData.kursstart || teilnehmerData.kursbeginn || teilnehmerData.additional_data?.Kursbeginn,
        einheiten: kursData.einheiten || teilnehmerData.einheiten || teilnehmerData.additional_data?.Einheiten,
        lehrer: kursData.lehrer || teilnehmerData.lehrer || teilnehmerData.additional_data?.Lehrer,
        // Kurs-Datei Infos
        kurs_file_url: kursData.file_url || null,
        kurs_file_name: kursData.file_name || null
      };
      
      // Also check if telefonnummer is in kurs
      if (kursData.telefonnummer && !flatData.additional_data?.Telefonnummer) {
        flatData.additional_data = {
          ...flatData.additional_data,
          Telefonnummer: kursData.telefonnummer
        };
      }
      
      console.log('Flattened Dashboard Data:', flatData);
      setDashboardData(flatData);
      setOtherAccounts(response.data.other_accounts || []);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Fehler beim Laden der Daten');
      if (error.response?.status === 401) {
        localStorage.removeItem('teilnehmer_token');
        navigate('/teilnehmer/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFixedTexts = async () => {
    try {
      const response = await axios.get(`${API}/public/fixed-texts`);
      setFixedTexts(response.data);
    } catch (error) {
      console.error('Error fetching fixed texts:', error);
    }
  };

  const fetchUITexts = async () => {
    try {
      const response = await axios.get(`${API}/public/ui-texts`);
      setUiTexts(response.data);
    } catch (error) {
      console.error('Error fetching UI texts:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('teilnehmer_token');
    toast.success('Erfolgreich abgemeldet');
    navigate('/teilnehmer/login');
  };

  const handleSwitchAccount = async (targetId) => {
    try {
      const token = localStorage.getItem('teilnehmer_token');
      const response = await axios.post(
        `${API}/teilnehmer/switch-account/${targetId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Save new token and reload
      localStorage.setItem('teilnehmer_token', response.data.access_token);
      toast.success('Account gewechselt');
      
      // Reload dashboard with new token
      setLoading(true);
      fetchDashboardData(response.data.access_token);
      fetchFixedTexts();
    } catch (error) {
      toast.error('Fehler beim Wechseln des Accounts');
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Kursdetails_${dashboardData?.vorname}_${dashboardData?.nachname}`,
  });

  const renderMarkdown = (text) => {
    if (!text) return null;
    
    let html = text
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Links
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>')
      // Line breaks
      .replace(/\n/g, '<br />');
    
    // Note: <code>, <span style="..."> are already HTML and will be rendered as-is
    
    return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Music className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-pulse" />
          <p className="text-gray-600">Lade deine Daten...</p>
        </div>
      </div>
    );
  }

  const isPruefungsModus = dashboardData?.modus === 'Prüfung';
  const hasExamResults = dashboardData?.pruefung_stufe || dashboardData?.pruefung_note || dashboardData?.pruefung_punkte;
  
  // Prüfe ob nicht bestanden (verwende die konfigurierbare Note aus fixedTexts)
  const nichtBestandenNote = fixedTexts.pruefung_nicht_bestanden_note || "Nicht genügend";
  const isNichtBestanden = dashboardData?.pruefung_note && 
    dashboardData.pruefung_note.toLowerCase().trim() === nichtBestandenNote.toLowerCase().trim();

  // Prüfungs-Modus OHNE Ergebnis: Hinweis anzeigen
  if (isPruefungsModus && !hasExamResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_musictheory-admin/artifacts/fetmifax_logo_standard.svg" 
                alt="MSY Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Musikschule Ybbsfeld</h1>
                <p className="text-sm text-gray-600">Prüfungsergebnis</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Abmelden
            </Button>
          </div>

          {/* Kein Ergebnis Banner */}
          <Card className="border-0 shadow-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white mb-8 overflow-hidden relative">
            <CardContent className="p-12 text-center relative z-10">
              <div className="text-6xl mb-6">⏳</div>
              <h1 className="text-4xl font-bold mb-4">Hallo {dashboardData.vorname}!</h1>
              <p className="text-xl opacity-90 leading-relaxed">
                {fixedTexts.pruefung_kein_ergebnis_text || "Dein Prüfungsergebnis ist noch nicht verfügbar. Bitte schau später noch einmal vorbei!"}
              </p>
            </CardContent>
          </Card>

          {/* Info Box */}
          <Card className="border border-gray-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="text-5xl mb-4">📋</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Noch etwas Geduld...</h2>
              <p className="text-gray-600 text-lg">
                Die Prüfungsergebnisse werden nach der Auswertung hier veröffentlicht. 
                Sobald dein Ergebnis vorliegt, kannst du es hier abrufen.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Prüfungs-Modus MIT Ergebnis: Verspielt & Feierlich
  if (isPruefungsModus && hasExamResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_musictheory-admin/artifacts/fetmifax_logo_standard.svg" 
                alt="MSY Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Musikschule Ybbsfeld</h1>
                <p className="text-sm text-gray-600">Dein Prüfungsergebnis</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Abmelden
            </Button>
          </div>

          {/* Banner - Unterschiedlich je nach Ergebnis */}
          {isNichtBestanden ? (
            <Card className="border-0 shadow-2xl bg-gradient-to-br from-orange-500 to-red-500 text-white mb-8 overflow-hidden relative">
              <CardContent className="p-12 text-center relative z-10">
                <div className="text-6xl mb-6">😢</div>
                <h1 className="text-4xl font-bold mb-4">{dashboardData.vorname} {dashboardData.nachname}</h1>
                <p className="text-xl opacity-90 leading-relaxed whitespace-pre-line">
                  {(fixedTexts.pruefung_nicht_bestanden_text || "Oje…dieses Mal hat es leider nicht geklappt.")
                    .replace('{lehrer}', dashboardData.lehrer || '')
                    .replace('{telefon}', dashboardData.additional_data?.Telefonnummer || '')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-2xl bg-gradient-to-br from-purple-600 to-pink-600 text-white mb-8 overflow-hidden relative">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 animate-bounce">
                  <Sparkles className="w-20 h-20" />
                </div>
                <div className="absolute bottom-10 right-10 animate-bounce" style={{ animationDelay: '0.5s' }}>
                  <Trophy className="w-24 h-24" />
                </div>
              </div>
              <CardContent className="p-12 text-center relative z-10">
                <Trophy className="w-24 h-24 mx-auto mb-6 animate-pulse" />
                <h1 className="text-5xl font-bold mb-4 animate-pulse">Herzlichen Glückwunsch!</h1>
                <p className="text-2xl mb-6">{dashboardData.vorname} {dashboardData.nachname}</p>
                <p className="text-xl opacity-90">Du hast die Prüfung erfolgreich abgeschlossen!</p>
              </CardContent>
            </Card>
          )}

          {/* Prüfungsergebnisse - Groß & Prominent */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {dashboardData.pruefung_stufe && (
              <Card className="border-0 shadow-xl bg-white hover:shadow-2xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <Award className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                  <p className="text-sm text-gray-600 mb-2">Stufe</p>
                  <p className="text-4xl font-bold text-purple-600">{dashboardData.pruefung_stufe}</p>
                </CardContent>
              </Card>
            )}
            {dashboardData.pruefung_punkte && (
              <Card className="border-0 shadow-xl bg-white hover:shadow-2xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 text-pink-600" />
                  <p className="text-sm text-gray-600 mb-2">Punkte</p>
                  <p className="text-4xl font-bold text-pink-600">{dashboardData.pruefung_punkte}</p>
                </CardContent>
              </Card>
            )}
            {dashboardData.pruefung_note && (
              <Card className="border-0 shadow-xl bg-white hover:shadow-2xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <Trophy className="w-12 h-12 mx-auto mb-4 text-yellow-600" />
                  <p className="text-sm text-gray-600 mb-2">Note</p>
                  <p className="text-4xl font-bold text-yellow-600">{dashboardData.pruefung_note}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Zusätzliche Infos */}
          {fixedTexts.pruefungseinleitung && (
            <Card className="border border-gray-200 shadow-lg">
              <CardContent className="p-8">
                <div className="prose max-w-none text-gray-700">
                  {renderMarkdown(fixedTexts.pruefungseinleitung)}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Textbausteine im Prüfungsmodus */}
          {dashboardData?.pruefung_textbausteine && dashboardData.pruefung_textbausteine.length > 0 && (
            <Card className="border border-gray-200 shadow-lg mt-8">
              <CardContent className="p-6 md:p-8 space-y-6">
                {dashboardData.pruefung_textbausteine.map((tb) => (
                  <div 
                    key={tb.kennung}
                    className={`
                      ${tb.has_border ? 'border-2 border-gray-900 rounded-lg p-4 md:p-5' : ''}
                      ${tb.background_color ? `bg-opacity-20` : ''}
                    `}
                    style={tb.background_color ? { backgroundColor: tb.background_color + '33' } : {}}
                  >
                    {tb.has_line_above && <hr className="border-t-2 border-gray-300 mb-4" />}
                    
                    {tb.show_title && tb.titel && (
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        {tb.emoji && <span>{tb.emoji}</span>}
                        {tb.titel}
                      </h3>
                    )}
                    
                    <div
                      className="prose prose-lg max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tb.inhalt) }}
                    />
                    
                    {/* File Download */}
                    {tb.file_url && tb.file_name && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${tb.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 hover:bg-blue-100 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        {tb.file_name}
                      </a>
                    )}
                    
                    {tb.has_line_below && <hr className="border-t-2 border-gray-300 mt-4" />}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Helper function to generate Google Maps link
  const getGoogleMapsLink = (address) => {
    if (!address) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  // Helper function to format date from yyyy-mm-dd or dd-mm-yyyy to dd.mm.yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Remove any existing separators and get numbers
    const cleaned = dateStr.replace(/[.\-\/]/g, '-');
    const parts = cleaned.split('-');
    
    if (parts.length === 3) {
      // Check if format is yyyy-mm-dd (year first)
      if (parts[0].length === 4) {
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
      }
      // Otherwise assume dd-mm-yyyy
      return `${parts[0]}.${parts[1]}.${parts[2]}`;
    }
    
    return dateStr; // Return original if can't parse
  };

  // Helper function to get file icon based on file type
  const getFileIcon = (fileName) => {
    if (!fileName) return '📄';
    const ext = fileName.toLowerCase().split('.').pop();
    
    switch(ext) {
      case 'pdf':
        return '📕';
      case 'doc':
      case 'docx':
        return '📘';
      case 'xls':
      case 'xlsx':
        return '📗';
      case 'ppt':
      case 'pptx':
        return '📙';
      case 'zip':
      case 'rar':
        return '📦';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      default:
        return '📄';
    }
  };

  // Anmeldungs-Modus: Clean & Übersichtlich mit Druckfunktion
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Druckbarer Content */}
      <div ref={printRef} className="max-w-4xl mx-auto px-6 py-8">
        {/* Logout Button - nur oben rechts */}
        <div className="flex justify-end mb-6 print:hidden">
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-500 hover:text-gray-700">
            <LogOut className="h-4 w-4 mr-2" />
            Abmelden
          </Button>
        </div>
        {/* Logo und Begrüßung */}
        <div className="text-center mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_musictheory-admin/artifacts/fetmifax_logo_standard.svg" 
            alt="Musikschule Ybbsfeld" 
            className="h-20 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {dashboardData?.vorname} {dashboardData?.nachname}
          </h1>
          {/* Optionaler Willkommenstext */}
          {fixedTexts.willkommenstext && (
            <p className="text-base text-gray-600 mb-4 max-w-2xl mx-auto">
              {fixedTexts.willkommenstext}
            </p>
          )}
          <p className="text-lg text-gray-600 mb-6">
            Du bist für folgenden Kurs angemeldet:
          </p>
        </div>

        {/* Kursname - Prominent */}
        {dashboardData?.theoriekurs && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center px-8 py-4 bg-blue-50 border-2 border-blue-200 rounded-xl shadow-md">
              <span className="text-2xl font-bold text-gray-900">
                Theoriekurs {dashboardData.theoriekurs}
              </span>
            </div>
          </div>
        )}

        {/* Mehrfach-Email Warnung - Dezent oben */}
        {otherAccounts.length > 0 && (
          <div className="mb-6 max-w-3xl mx-auto print:hidden">
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <svg className="h-4 w-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-amber-800">
                    Mit dieser E-Mail sind mehrere Personen angemeldet.
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {otherAccounts.map((account) => (
                    <Button
                        key={account.id}
                        onClick={() => handleSwitchAccount(account.id)}
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2 border-amber-400 text-amber-800 hover:bg-amber-100"
                      >
                        {account.vorname} {account.nachname}
                      </Button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Einleitungstext */}
        {fixedTexts.info_text && (
          <div className="mb-8 text-center max-w-3xl mx-auto">
            <div className="text-base text-gray-700 leading-relaxed">
              {renderMarkdown(fixedTexts.info_text)}
            </div>
          </div>
        )}

        {/* Kursinformationen */}
        <div className="mb-8">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4">Kursinformationen</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {/* Kursbeginn */}
            {dashboardData?.kursbeginn && (
              <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-white rounded-lg border border-gray-300 shadow-sm">
                <Calendar className="h-4 w-4 md:h-5 md:w-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase">Kursbeginn</p>
                  <p className="text-sm md:text-base font-bold text-gray-900">{formatDate(dashboardData.kursbeginn)}</p>
                </div>
              </div>
            )}

            {/* Wochentag */}
            {dashboardData?.wochentag && (
              <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-white rounded-lg border border-gray-300 shadow-sm">
                <Calendar className="h-4 w-4 md:h-5 md:w-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase">Wochentag</p>
                  <p className="text-sm md:text-base font-bold text-gray-900">{dashboardData.wochentag}</p>
                </div>
              </div>
            )}

            {/* Uhrzeit */}
            {dashboardData?.uhrzeit && (
              <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-white rounded-lg border border-gray-300 shadow-sm">
                <Clock className="h-4 w-4 md:h-5 md:w-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase">Uhrzeit</p>
                  <p className="text-sm md:text-base font-bold text-gray-900">{dashboardData.uhrzeit}</p>
                </div>
              </div>
            )}

            {/* Einheiten */}
            {dashboardData?.einheiten && (
              <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-white rounded-lg border border-gray-300 shadow-sm">
                <Calendar className="h-4 w-4 md:h-5 md:w-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase">Einheiten</p>
                  <p className="text-sm md:text-base font-bold text-gray-900">{dashboardData.einheiten}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Zweite Reihe: Lehrer und Ort */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-3 md:mt-4">
            {/* Lehrer */}
            {dashboardData?.lehrer && (
              <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-white rounded-lg border border-gray-300 shadow-sm">
                <User className="h-4 w-4 md:h-5 md:w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase">Lehrer/in</p>
                  <p className="text-sm md:text-lg font-bold text-gray-900">{dashboardData.lehrer}</p>
                  {dashboardData.additional_data?.Telefonnummer && (
                    <a 
                      href={`tel:${dashboardData.additional_data.Telefonnummer.replace(/[^0-9+]/g, '')}`}
                      className="inline-flex items-center gap-1 text-xs md:text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline print:hidden mt-1"
                    >
                      <svg className="h-3 w-3 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {dashboardData.additional_data.Telefonnummer}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Ort */}
            {dashboardData?.kursort && (
              <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-white rounded-lg border border-gray-300 shadow-sm">
                <MapPin className="h-4 w-4 md:h-5 md:w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase">Kursort</p>
                  <p className="text-sm md:text-lg font-bold text-gray-900">{dashboardData.kursort}</p>
                  <a 
                    href={dashboardData.kursort_link || getGoogleMapsLink(dashboardData.kursort)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs md:text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline print:hidden mt-1"
                  >
                    <MapPin className="h-3 w-3 md:h-4 md:w-4" />
                    In Google Maps öffnen
                  </a>
                </div>
              </div>
            )}
          </div>
          
          {/* Terminliste - Eigenes Feld unterhalb */}
          {dashboardData?.kurs_file_url && (
            <div className="mt-3 md:mt-4 flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200 shadow-sm">
              <Download className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] md:text-xs text-blue-600 uppercase font-medium">Terminliste</p>
                <a 
                  href={`${BACKEND_URL}${dashboardData.kurs_file_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm md:text-base font-bold text-blue-700 hover:text-blue-800 hover:underline print:hidden"
                >
                  {dashboardData.kurs_file_name || 'Terminliste herunterladen'}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Textbausteine - NUR im Anmeldungsmodus anzeigen */}
        {!isPruefungsModus && dashboardData?.textbausteine && dashboardData.textbausteine.length > 0 && (
          <div className="mb-8">
            {/* Gemeinsamer Kasten für alle Textbausteine */}
            <div className="border-2 border-gray-300 rounded-xl bg-white shadow-sm overflow-hidden">
              {/* Kasten-Überschrift */}
              <div className="bg-gray-100 border-b border-gray-300 px-4 md:px-6 py-3 md:py-4">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">Wichtige Informationen</h2>
              </div>
              
              {/* Textbausteine innerhalb des Kastens */}
              <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                {dashboardData.textbausteine.map((tb, idx) => (
                  <div key={idx}>
                    {/* Linie oben */}
                    {tb.has_line_above && (
                      <hr className="border-t-2 border-gray-400 mb-4" />
                    )}
                    <div 
                      className={`${
                        tb.has_border ? 'border-2 border-gray-900 rounded-lg p-4 md:p-5' : ''
                      } ${tb.background_color ? 'rounded-lg p-4 md:p-5' : ''}`}
                      style={{
                        backgroundColor: tb.background_color || undefined
                      }}
                    >
                    {/* Überschrift wenn vorhanden - mit optionalem Emoji */}
                    {tb.show_title && tb.titel && (
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        {tb.emoji && <span className="text-xl md:text-2xl">{tb.emoji}</span>}
                        {tb.titel}
                      </h3>
                    )}
                    
                    {/* Inhalt */}
                    <div className="prose md:prose-lg max-w-none text-gray-700 leading-relaxed">
                      {renderMarkdown(tb.inhalt)}
                    </div>
                    
                    {/* Code-Block */}
                    {tb.code && (
                      <div className="mt-4 p-5 md:p-6 bg-gray-900 rounded-lg border border-gray-700 overflow-x-auto">
                        <pre className="font-mono text-base md:text-lg text-green-400 whitespace-pre-wrap leading-relaxed">
                          {tb.code}
                        </pre>
                      </div>
                    )}
                    
                    {/* Datei-Download */}
                    {tb.file_url && (
                      <a
                        href={`${BACKEND_URL}${tb.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors print:hidden font-medium"
                      >
                        <span className="text-xl">{getFileIcon(tb.file_name)}</span>
                        <span>{tb.file_name || 'Anhang herunterladen'}</span>
                        <Download className="h-4 w-4 ml-1" />
                      </a>
                    )}
                    </div>
                    {/* Linie unten */}
                    {tb.has_line_below && (
                      <hr className="border-t-2 border-gray-400 mt-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Print Footer */}
        <div className="hidden print:block mt-12 pt-6 border-t border-gray-300 text-center text-sm text-gray-500">
          <p>Musikschule Ybbsfeld - Theoriekurs Verwaltung</p>
          <p className="mt-1">Gedruckt am: {new Date().toLocaleDateString('de-DE')}</p>
        </div>
      </div>
    </div>
  );
};

export default TeilnehmerDashboard;